import { Controller } from "@hotwired/stimulus"
import * as echarts from "echarts"

export default class extends Controller {
  static values = {
    title: String,
    items: Array
  }

  connect() {
    this.render()
    this.handleResize = this.resize.bind(this)
    window.addEventListener("resize", this.handleResize)
  }

  disconnect() {
    window.removeEventListener("resize", this.handleResize)
    this.disposeChart()
  }

  render() {
    if (!Array.isArray(this.itemsValue) || this.itemsValue.length === 0) {
      this.disposeChart()
      this.element.innerHTML = '<div class="text-sm text-base-content/60 py-16 text-center">No status data available.</div>'
      return
    }

    this.element.innerHTML = ""
    this.chart ||= echarts.init(this.element)
    const coloredData = this.applyBadgeColors(this.itemsValue)

    this.chart.setOption({
      tooltip: {
        trigger: "item",
        formatter: (params) => {
          const data = params.data || {}
          const name = this.escapeHtml(data.name || "Unknown")
          const percentage = Number(data.percentage || 0).toFixed(1)
          const count = Number(data.count || 0)
          return `<strong>${name}</strong><br/>${percentage}%<br/>${count} records`
        }
      },
      series: [
        {
          type: "treemap",
          left: 0,
          top: 0,
          right: 0,
          bottom: 0,
          width: "100%",
          height: "100%",
          roam: false,
          nodeClick: false,
          emphasis: {
            // Prevent default hover color shift that can wash tiles to white.
            disabled: true
          },
          breadcrumb: { show: false, height: 0 },
          upperLabel: { show: false },
          label: {
            show: true,
            formatter: (params) => {
              const data = params.data || {}
              const name = data.name || "Unknown"
              const percentage = Number(data.percentage || 0).toFixed(1)
              return `${name}\n${percentage}%`
            }
          },
          itemStyle: {
            borderColor: "rgba(255,255,255,0.25)",
            borderWidth: 1,
            gapWidth: 2
          },
          levels: [
            {
              color: [],
              colorMappingBy: "index"
            }
          ],
          data: coloredData
        }
      ]
    })
  }

  resize() {
    if (this.chart) this.chart.resize()
  }

  disposeChart() {
    if (!this.chart) return

    this.chart.dispose()
    this.chart = null
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }

  applyBadgeColors(items) {
    return items.map((item) => {
      const badgeClass = item.badge_class
      if (!badgeClass) return item

      const resolvedColor = this.resolveColorFromBadgeClass(badgeClass)
      if (!resolvedColor) return item

      return { ...item, itemStyle: { color: resolvedColor } }
    })
  }

  resolveColorFromBadgeClass(badgeClass) {
    const probe = document.createElement("span")
    probe.className = `badge badge-sm ${badgeClass}`
    probe.style.position = "absolute"
    probe.style.left = "-9999px"
    probe.style.top = "-9999px"
    probe.style.visibility = "hidden"
    probe.textContent = "x"
    document.body.appendChild(probe)

    const styles = window.getComputedStyle(probe)
    const background = styles.backgroundColor
    const border = styles.borderColor
    const foreground = styles.color
    probe.remove()

    if (!this.isTransparent(background)) return background
    if (!this.isTransparent(border)) return border
    if (!this.isTransparent(foreground)) return foreground
    return null
  }

  isTransparent(color) {
    if (!color) return true
    if (color === "transparent") return true

    // rgba(0, 0, 0, 0)
    const rgba = color.match(/rgba?\(([^)]+)\)/)
    if (!rgba) return false

    const parts = rgba[1].split(",").map((part) => part.trim())
    if (parts.length < 4) return false
    return Number(parts[3]) === 0
  }
}
